import { useEffect, useState } from 'react';
import {
    Paper, Title, Text, Button, Group, Modal, Tabs, ActionIcon,
} from '@mantine/core';
import {
    IconPlus, IconLogout, IconCar, IconClipboardList, IconMessageCircle,
} from '@tabler/icons-react';
import MagicLinkForm from './MagicLinkForm';
import ServiceRequestForm from './ServiceRequestForm';
import ServiceRequestList from './ServiceRequestList';
import ChatScreen from './ChatScreen';
import FleetOverview from './FleetOverview';
import VehicleDetail from './VehicleDetail';
import { loadSession, saveSession, clearSession, cfg } from './session';

export default function ServiceOnlyApp() {
    const [session, setSession] = useState(null);
    const [phase, setPhase] = useState('init');
    const [tab, setTab] = useState('vehicles');
    const [selectedAmbulance, setSelectedAmbulance] = useState(null);
    const [scheduleFor, setScheduleFor] = useState(undefined); // undefined = closed; null/id = open
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        const url = new URL(window.location.href);
        const magicToken = url.searchParams.get('token');

        if (magicToken) {
            setPhase('exchanging');
            fetch(`${cfg.apiUrl}/support/validate-token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: magicToken }),
            })
                .then(async (res) => {
                    const body = await res.json();
                    if (!res.ok) throw new Error(body.message);
                    return body;
                })
                .then((data) => {
                    saveSession(data);
                    setSession(data);
                    setPhase('ready');
                    url.searchParams.delete('token');
                    window.history.replaceState({}, '', url.toString());
                })
                .catch(() => setPhase('login'));
            return;
        }
        const existing = loadSession();
        if (existing) {
            setSession(existing);
            setPhase('ready');
        } else {
            setPhase('login');
        }
    }, []);

    function handleLogout() {
        clearSession();
        setSession(null);
        setPhase('login');
    }

    function openSchedule(ambulanceId) {
        setScheduleFor(ambulanceId ?? null);
    }

    if (phase === 'init' || phase === 'exchanging') {
        return <Text ta="center" c="dimmed" p="xl">Bezig...</Text>;
    }
    if (phase === 'login' || !session) {
        return <MagicLinkForm onLoggedIn={(d) => { saveSession(d); setSession(d); setPhase('ready'); }} />;
    }

    const customerLabel = session.customer
        ? (session.customer.company_name
            || `${session.customer.first_name || ''} ${session.customer.last_name || ''}`.trim()
            || session.customer.email)
        : 'Klant';

    return (
        <div style={{ maxWidth: 1280, margin: '24px auto', padding: '0 16px' }}>
            <Group justify="space-between" mb="md">
                <div>
                    <Title order={3}>Mijn service</Title>
                    <Text size="xs" c="dimmed">Ingelogd als {customerLabel}</Text>
                </div>
                <Group>
                    <Button leftSection={<IconPlus size={16} />} onClick={() => openSchedule(null)}>
                        Nieuwe aanvraag
                    </Button>
                    <ActionIcon variant="subtle" color="gray" onClick={handleLogout} title="Uitloggen">
                        <IconLogout size={18} />
                    </ActionIcon>
                </Group>
            </Group>

            <Tabs value={tab} onChange={(v) => { setTab(v); if (v !== 'vehicles') setSelectedAmbulance(null); }}>
                <Tabs.List mb="md">
                    <Tabs.Tab value="vehicles" leftSection={<IconCar size={16} />}>Voertuigen</Tabs.Tab>
                    <Tabs.Tab value="requests" leftSection={<IconClipboardList size={16} />}>Aanvragen</Tabs.Tab>
                    <Tabs.Tab value="chat" leftSection={<IconMessageCircle size={16} />}>Chat</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="vehicles">
                    {selectedAmbulance ? (
                        <VehicleDetail
                            ambulanceId={selectedAmbulance}
                            onBack={() => setSelectedAmbulance(null)}
                            onSchedule={openSchedule}
                        />
                    ) : (
                        <FleetOverview onSelect={setSelectedAmbulance} />
                    )}
                </Tabs.Panel>

                <Tabs.Panel value="requests">
                    <Paper withBorder p="md">
                        <Title order={5} mb="sm">Service-aanvragen</Title>
                        <ServiceRequestList key={refreshKey} />
                    </Paper>
                </Tabs.Panel>

                <Tabs.Panel value="chat">
                    <Paper withBorder style={{
                        height: 'min(640px, calc(100vh - 240px))',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        <ChatScreen session={session} onLogout={handleLogout} embedded />
                    </Paper>
                </Tabs.Panel>
            </Tabs>

            <Modal
                opened={scheduleFor !== undefined}
                onClose={() => setScheduleFor(undefined)}
                size="lg"
                title="Service inplannen / nieuwe aanvraag"
            >
                <ServiceRequestForm
                    prefilledAmbulanceId={scheduleFor ? String(scheduleFor) : ''}
                    onClose={() => setScheduleFor(undefined)}
                    onSubmitted={() => {
                        setScheduleFor(undefined);
                        setRefreshKey((k) => k + 1);
                        setTab('requests');
                    }}
                />
            </Modal>
        </div>
    );
}
